import { normaliseJobStatus } from './utils'

export const customerDisplayName = customer => [customer?.first_name, customer?.last_name].filter(Boolean).join(' ').trim() || customer?.email || 'Customer'
export const phoneDigits = value => String(value || '').replace(/\D/g, '')

export function compareCustomerNames(a, b) {
  return customerDisplayName(a).localeCompare(customerDisplayName(b), 'en', { sensitivity: 'base' })
    || String(a.id).localeCompare(String(b.id))
}

export function customerMatches(customer, query) {
  const text = String(query || '').trim().toLowerCase()
  const digits = phoneDigits(text)
  return !text || customerDisplayName(customer).toLowerCase().includes(text)
    || String(customer.email || '').toLowerCase().includes(text)
    || (digits.length > 0 && /^[\d\s()+.\-]+$/.test(text) && phoneDigits(customer.phone).includes(digits))
}

// Advance by the actual page length, including when the server cap is smaller
// than our requested page. An empty page ends the directory; failures never
// return a partial directory as if it were complete.
export async function loadWorkshopRows(client, table, workshopId, columns = '*', signal, customerId = '') {
  if (!workshopId) return []
  const rows = []
  const ids = new Set()
  let expectedCount = null
  for (;;) {
    if (signal?.aborted) throw new Error('Loading cancelled.')
    let query = client.from(table).select(columns, { count: 'exact' }).eq('workshop_id', workshopId)
      .order('id').range(rows.length, rows.length + 249)
    if (customerId) query = query.eq('customer_id', customerId)
    if (signal) query = query.abortSignal(signal)
    const { data, error, count } = await query
    if (error) throw error
    if (typeof count === 'number') {
      if (expectedCount !== null && expectedCount !== count) throw new Error('The directory changed while loading. Please retry.')
      expectedCount = count
    }
    if (!data?.length) {
      if (expectedCount !== null && rows.length !== expectedCount) throw new Error('The directory is incomplete. Please retry.')
      return rows
    }
    for (const row of data) {
      if (ids.has(row.id)) throw new Error('The directory changed while loading. Please retry.')
      ids.add(row.id)
      rows.push(row)
    }
    if (expectedCount !== null && rows.length >= expectedCount) return rows
  }
}

// loadWorkshopRows in a Supabase-style { data, error } result, so screens can
// load it alongside ordinary queries. Rows come back ordered by id; pass a
// comparator to restore the order the screen expects.
export function workshopRowsResult(client, table, workshopId, columns = '*', compare = null) {
  return loadWorkshopRows(client, table, workshopId, columns)
    .then(rows => ({ data: compare ? rows.sort(compare) : rows, error: null }), error => ({ data: null, error }))
}

export const newestJobFirst = (a, b) => (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0)
export const byLastName = (a, b) => String(a.last_name || '').localeCompare(String(b.last_name || ''), 'en', { sensitivity: 'base' })

export const CUSTOMER_DIRECTORY_COLUMNS = 'id,workshop_id,first_name,last_name,email,phone'

export function customerJobActivity(jobs) {
  const activity = new Map()
  jobs.forEach(job => {
    const current = activity.get(job.customer_id) || { recent: 0, open: false }
    current.recent = Math.max(current.recent, Date.parse(job.date || job.created_at || '') || 0)
    current.open ||= !['completed', 'cancelled', 'voided'].includes(normaliseJobStatus(job.status))
    activity.set(job.customer_id, current)
  })
  return activity
}

export function sortCustomers(customers, sort, activity = new Map()) {
  return [...customers].sort((a, b) => {
    if (sort === 'recent_job') return ((activity.get(b.id)?.recent || 0) - (activity.get(a.id)?.recent || 0)) || compareCustomerNames(a, b)
    if (sort === 'recent_added') return ((Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0)) || compareCustomerNames(a, b)
    if (sort === 'name_desc') return customerDisplayName(b).localeCompare(customerDisplayName(a), 'en', { sensitivity: 'base' }) || String(a.id).localeCompare(String(b.id))
    return compareCustomerNames(a, b)
  })
}
